import { LEOKIT_BASE } from "../lib/leokit-base";
import { EVM_CHAINS, type EvmChainId } from "./chains";

/**
 * Wraps LeoKit's `/leokit/balances` POST endpoint. We send one
 * `{ chain, address }` row per EVM chain we care about and reshape
 * the response into a flat, UI-friendly structure.
 *
 * In dev the proxy in `vite.config.ts` rewrites `/api/leokit/*` →
 * `api.leokit.dev/*` and injects the `Api-Key` header. In prod the
 * same proxy is expected from the deployment's nginx. Override the
 * base via `VITE_LEOKIT_BASE_URL` if needed.
 */

// LEOKIT_BASE now lives in `../lib/leokit-base` so the EVM module and
// the LeoKit swap client share one source of truth.

export interface TokenBalance {
  /** Full LeoKit asset id, e.g. `"ETH.USDC-0xA0b8…"`. */
  token_symbol: string;
  /** Display ticker pulled out of `token_symbol`, e.g. `"USDC"`. */
  symbol: string;
  /** Already-decimalised balance. */
  balance: number;
  balance_usd: number;
  price: number;
  icon?: string;
}

export interface ChainBalances {
  chain: EvmChainId;
  chainName: string;
  balance_usd: number;
  tokens: TokenBalance[];
}

export interface EvmHoldings {
  address: `0x${string}`;
  total_usd: number;
  perChain: ChainBalances[];
  /** Wall-clock ms when this snapshot was fetched. */
  fetchedAt: number;
}

// Tolerant of extra properties — LeoKit's row shape is broader than we
// need, and we only pluck what the UI uses.
interface LeokitWalletRow {
  chain: string;
  balance_usd?: number;
  balances?: Array<{
    token_symbol: string;
    balance: number;
    balance_usd: number;
    price?: number;
    icon?: string;
  }>;
}

interface LeokitBalancesResponse {
  wallets?: LeokitWalletRow[];
}

/** `"ETH.USDC-0xA0b8…"` → `"USDC"`. Falls back to the raw id on weird input. */
const symbolFrom = (tokenSymbol: string): string => {
  const after = tokenSymbol.split(".")[1] ?? tokenSymbol;
  return after.split("-")[0] ?? after;
};

export async function fetchEvmHoldings(
  address: `0x${string}`,
  signal?: AbortSignal,
): Promise<EvmHoldings> {
  const body = EVM_CHAINS.map((c) => ({ chain: c.id, address }));
  // LeoKit's FileSystemRouter mounts `routes/leokit/balances.tsx` at
  // `/balances`, NOT `/leokit/balances` — the `routes/` segment is
  // stripped but the next-level "leokit" subdir is too. Verified
  // empirically against api.leokit.dev: only `/balances` works.
  const res = await fetch(`${LEOKIT_BASE}/balances`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    throw new Error(`LeoKit /balances → HTTP ${res.status}`);
  }
  const data = (await res.json()) as LeokitBalancesResponse;
  const wallets = data.wallets ?? [];

  // Always emit one entry per EVM chain in our list, even if LeoKit
  // filtered out chains with no balance. Keeps the UI layout stable.
  const perChain: ChainBalances[] = EVM_CHAINS.map((c) => {
    const row = wallets.find((w) => w.chain?.toUpperCase() === c.id);
    const tokens = (row?.balances ?? [])
      .map((b) => ({
        token_symbol: b.token_symbol,
        symbol: symbolFrom(b.token_symbol),
        balance: b.balance,
        balance_usd: b.balance_usd,
        price: b.price ?? 0,
        icon: b.icon,
      }))
      .sort((a, b) => b.balance_usd - a.balance_usd);
    return {
      chain: c.id,
      chainName: c.name,
      balance_usd: row?.balance_usd ?? 0,
      tokens,
    };
  });

  return {
    address,
    total_usd: perChain.reduce((s, c) => s + c.balance_usd, 0),
    perChain,
    fetchedAt: Date.now(),
  };
}
